/**
 * Date and time utility functions using date-fns library.
 * Provides consistent date/time formatting across the application.
 */

import { differenceInCalendarDays, format, isValid, parseISO } from 'date-fns';

/**
 * Format time in the timezone specified in the ISO string
 * @param timeString - ISO time string with timezone (e.g., "2024-03-15T22:30:00-05:00")
 * @param formatString - date-fns format string
 * @returns Formatted time in the original timezone
 */
export function formatTimeInOriginalTimezone(timeString: string, formatString: string = 'h:mm a'): string {
  if (!timeString) return '';

  try {
    // Strip timezone from ISO string (remove everything after the seconds)
    const timeWithoutTimezone = timeString.replace(/[+-]\d{2}:\d{2}$|Z$/, '');

    // Format the time using date-fns
    return formatTime(timeWithoutTimezone, formatString);
  } catch (error) {
    console.warn(`Failed to format time in original timezone:`, error);
    return formatTime(timeString, formatString);
  }
}

/**
 * Format time in airport's local timezone
 * @param timeString - ISO time string
 * @param timeZone - Timezone
 * @param formatString - date-fns format string
 * @returns Formatted time in airport's timezone
 */
export function formatTimeInTimezone(timeString: string, timeZone: string, formatString: string = 'h:mm a'): string {
  if (!timeString || !timeZone) return '';

  try {
    const date = parseISO(timeString);

    // Use Intl.DateTimeFormat to format in the airport's timezone
    return new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: formatString.includes('a'),
    }).format(date);
  } catch (error) {
    console.warn(`Failed to format time in ${timeZone}:`, error);
    return formatTime(timeString, formatString);
  }
}

/**
 * Formats a time string to the specified format.
 * Handles various input formats including ISO strings and HH:MM strings.
 *
 * @param timeString - The time string to format
 * @param formatString - date-fns format string (default: 'HH:mm')
 * @returns Formatted time string, or original string if parsing fails
 */
export function formatTime(timeString: string, formatString: string = 'HH:mm'): string {
  if (!timeString) return '';

  try {
    // Try parsing as ISO string first
    let date: Date;

    if (timeString.includes('T') || timeString.includes('Z')) {
      // ISO format
      date = parseISO(timeString);
    } else if (/^\d{1,2}:\d{2}$/.test(timeString)) {
      // HH:MM format - create date with today's date
      const [hours, minutes] = timeString.split(':').map(Number);
      if (hours !== undefined && minutes !== undefined) {
        date = new Date();
        date.setHours(hours, minutes, 0, 0);
      } else {
        date = new Date(timeString);
      }
    } else {
      // Try parsing as general date
      date = new Date(timeString);
    }

    if (isValid(date)) {
      return format(date, formatString);
    }
  } catch {
    // If parsing fails, return original string
  }

  return timeString;
}

/**
 * Formats a duration in minutes to a human-readable string.
 *
 * @param minutes - Duration in minutes
 * @returns Formatted duration string (e.g., "2h 30m", "45m", "1h")
 */
export function formatDuration(minutes: number): string {
  if (!minutes || minutes < 0) return '';

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours > 0) {
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
  return `${mins}m`;
}

/**
 * Formats a date to a readable string format.
 *
 * @param dateInput - Date string, Date object, or timestamp
 * @param formatString - date-fns format string (default: 'MMM d, yyyy')
 * @returns Formatted date string
 */
export function formatDate(dateInput: string | Date | number, formatString: string = 'MMM d, yyyy'): string {
  try {
    let date: Date;

    if (typeof dateInput === 'string') {
      // Strip time and timezone info from date string before formatting
      const dateOnly = extractDatePart(dateInput) || dateInput;
      date = parseISO(dateOnly);
    } else if (typeof dateInput === 'number') {
      date = new Date(dateInput);
    } else {
      date = dateInput;
    }

    if (isValid(date)) {
      return format(date, formatString);
    }
  } catch {
    // If parsing fails, return empty string
  }

  return '';
}

/**
 * Formats a date and time together.
 *
 * @param dateTimeInput - Date/time string or Date object
 * @param dateFormat - Date format string (default: 'MMM d, yyyy')
 * @param timeFormat - Time format string (default: 'HH:mm')
 * @returns Formatted date and time string
 */
export function formatDateTime(
  dateTimeInput: string | Date | number,
  dateFormat: string = 'MMM d, yyyy',
  timeFormat: string = 'HH:mm',
): string {
  try {
    let date: Date;

    if (typeof dateTimeInput === 'string') {
      date = parseISO(dateTimeInput);
    } else if (typeof dateTimeInput === 'number') {
      date = new Date(dateTimeInput);
    } else {
      date = dateTimeInput;
    }

    if (isValid(date)) {
      const dateStr = format(date, dateFormat);
      const timeStr = format(date, timeFormat);
      return `${dateStr} at ${timeStr}`;
    }
  } catch {
    // If parsing fails, return empty string
  }

  return '';
}

/**
 * Formats a date range with the year only shown on the end date.
 *
 * @param startDate - Start date as ISO date string (YYYY-MM-DD)
 * @param endDate - End date as ISO date string (YYYY-MM-DD)
 * @returns Formatted date range string (e.g., "Friday 15th March → Sunday 17th March, 2024")
 */
export function formatDateRange(startDate: string, endDate: string, displayDayOfWeek: boolean = false): string {
  if (!startDate || !endDate) return '';

  const startDateObj = toDate(startDate);
  const endDateObj = toDate(endDate);

  if (!startDateObj || !endDateObj) return '';

  const isSameMonth = startDateObj.getMonth() === endDateObj.getMonth();
  const isSameYear = startDateObj.getFullYear() === endDateObj.getFullYear();
  const isSameDay = startDateObj.getDate() === endDateObj.getDate() && startDateObj.getMonth() === endDateObj.getMonth() && startDateObj.getFullYear() === endDateObj.getFullYear();
  // Drop the year when the whole range falls in the current year — redundant context the way calendar
  // UIs omit it ("June 8th → 15th"); ranges in another year (or spanning years) still show it.
  const currentYear = new Date().getFullYear();
  const showYear = startDateObj.getFullYear() !== currentYear || endDateObj.getFullYear() !== currentYear;

  if (isSameDay) {
    const base = displayDayOfWeek ? 'EEEE MMMM do' : 'MMMM do';
    return formatDate(startDate, showYear ? `${base}, yyyy` : base);
  }

  let startFormat = displayDayOfWeek ? 'EEEE, MMMM do' : 'MMMM do';
  let endFormat = displayDayOfWeek ? 'EEEE, do' : 'do';

  if (!(isSameMonth && isSameYear)) {
    endFormat = 'MMMM ' + endFormat;
  }

  if (showYear) {
    endFormat += ', yyyy';
    if (!isSameYear) {
      startFormat += ', yyyy';
    }
  }

  return `${formatDate(startDate, startFormat)} → ${formatDate(endDate, endFormat)}`;
}

/**
 * Formats a date range to a short string format.
 * 
 * @param startDate - Start date as ISO date string (YYYY-MM-DD)
 * @param endDate - End date as ISO date string (YYYY-MM-DD)
 * @returns Formatted short date range string (e.g., "15th March → 17th March, 2024")
 */
export function formatShortDateRange(startDate: string, endDate: string): string {
  if (!startDate || !endDate) return '';

  const startDateObj = toDate(startDate);
  const endDateObj = toDate(endDate);

  if (!startDateObj || !endDateObj) return '';

  const isSameMonth = startDateObj.getMonth() === endDateObj.getMonth();
  const isSameYear = startDateObj.getFullYear() === endDateObj.getFullYear();
  const isSameDay = startDateObj.getDate() === endDateObj.getDate() && startDateObj.getMonth() === endDateObj.getMonth() && startDateObj.getFullYear() === endDateObj.getFullYear();
  // Drop the year when the whole range falls in the current year (see formatDateRange).
  const currentYear = new Date().getFullYear();
  const showYear = startDateObj.getFullYear() !== currentYear || endDateObj.getFullYear() !== currentYear;

  if (isSameDay) {
    return formatDate(startDateObj, showYear ? 'MMM d, yyyy' : 'MMM d');
  }

  let startFormat = 'MMM d';
  let endFormat = 'd';

  if (!(isSameMonth && isSameYear)) {
    endFormat = 'MMM ' + endFormat;
  }

  if (showYear) {
    endFormat += ', yyyy';
    if (!isSameYear) {
      startFormat += ', yyyy';
    }
  }

  return `${formatDate(startDate, startFormat)} → ${formatDate(endDate, endFormat)}`;
}

/**
 * Extracts the date part (YYYY-MM-DD) from a datetime string.
 * Handles multiple formats:
 * - ISO datetime with 'T': "2024-01-15T10:30:00Z" → "2024-01-15"
 * - Local datetime with space: "2025-11-02 12:35" → "2025-11-02"
 * - Date-only: "2024-01-15" → "2024-01-15"
 *
 * @param dateTimeString - The datetime string to extract date from
 * @returns The date part (YYYY-MM-DD) if valid, or undefined if invalid
 */
export function extractDatePart(dateTimeString: string): string | undefined {
  if (!dateTimeString) return;

  let datePart: string;
  if (dateTimeString.includes('T')) {
    datePart = dateTimeString.split('T')[0]!;
  } else if (dateTimeString.includes(' ')) {
    datePart = dateTimeString.split(' ')[0]!;
  } else {
    datePart = dateTimeString;
  }

  // Validate that it matches YYYY-MM-DD format
  if (datePart && /^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
    return datePart;
  }

  return;
}

/**
 * Whole calendar days from today until `dateInput` — the "days to go" a traveler counts, not a
 * 24-hour-block difference (a flight tomorrow morning is 1, however few hours away it is).
 *
 * @param dateInput - The target date (string, Date, Timestamp, or number)
 * @returns Days until the date (0 = today, negative = already past), or undefined if unparseable
 *
 * @example
 * daysUntil('2026-08-14T09:00:00Z'); // 16, when today is 2026-07-29
 */
export function daysUntil(dateInput: unknown): number | undefined {
  const target = toDate(dateInput);
  if (!target) return;
  return differenceInCalendarDays(target, new Date());
}

/**
 * Formats the time elapsed since a given date as a human-readable string.
 *
 * @param date - The date to calculate elapsed time from
 * @returns Formatted string (e.g., "2h ago", "1d ago", "3m ago")
 */
export function formatTimeSince(date: Date | string | number): string {
  const d = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  if (!isValid(d)) return '';

  const ms = Date.now() - d.getTime();
  const minutes = Math.floor(ms / 60_000);
  const hours = Math.floor(ms / 3_600_000);
  const days = Math.floor(ms / 86_400_000);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'just now';
}

/**
 * Safely converts a date value to a Date object.
 * Handles strings, Date objects, Timestamps (Firestore), and numbers.
 *
 * @param dateInput - The date value to convert (string, Date, Timestamp, or number)
 * @returns A valid Date object, or undefined if conversion fails
 */
export function toDate(dateInput: unknown): Date | undefined {
  if (!dateInput) return;

  try {
    // Handle Date objects
    if (dateInput instanceof Date) {
      return isValid(dateInput) ? dateInput : undefined;
    }

    // Handle Timestamp objects (Firestore Timestamp with toDate method)
    if (typeof dateInput === 'object' && 'toDate' in dateInput && typeof dateInput.toDate === 'function') {
      const date = (dateInput as { toDate: () => Date }).toDate();
      return isValid(date) ? date : undefined;
    }

    // Handle strings
    if (typeof dateInput === 'string') {
      const date = parseISO(dateInput);
      return isValid(date) ? date : undefined;
    }

    // Handle numbers (timestamps)
    if (typeof dateInput === 'number') {
      const date = new Date(dateInput);
      return isValid(date) ? date : undefined;
    }
  } catch {
    // If conversion fails, return undefined
  }

  return;
}

/** Converts a date-ish value (Date, Firestore Timestamp, ISO string, number) to ms since epoch, or 0 if unparseable. */
export function toTimestampMs(dateInput: unknown): number {
  return toDate(dateInput)?.getTime() ?? 0;
}
